import React, { FC } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from 'recharts';
import { useContext } from 'react';
import { appContext } from '../../App';

interface DataItem {
    time: string;
    hashrate: number;
}

interface SynchronizedAreaChart2Props {
    data: DataItem[];
}

const SynchronizedAreaChart2: FC<SynchronizedAreaChart2Props> = ({ data }) => {
    const { darkMode } = useContext(appContext);

    const maxValue = Math.max(...data.map((item) => item.hashrate));
    const interval = Math.max(1, Math.ceil(maxValue / 5));
    const ticks = Array.from(
        { length: Math.ceil(maxValue / interval) + 1 },
        (_, i) => i * interval
    );

    const chartColors = {
        stroke: darkMode ? '#38bdf8' : '#3b82f6',
        fill: darkMode ? '#0ea5e9' : '#3b82f6',
        grid: darkMode ? '#334155' : '#f1f5f9',
        text: darkMode ? '#94a3b8' : '#64748b',
    };


    if (!data || data.length === 0) {
        return (
            <div className="w-full flex items-center justify-center h-[300px] md:h-[400px]">
                <div role="status" className="animate-pulse flex flex-col items-center gap-4">
                    <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                    <div className="h-64 w-full md:w-[600px] bg-slate-100 dark:bg-slate-800 rounded-2xl"></div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            <div className='mx-auto text-xs md:p-0 h-[300px] md:h-[400px]'>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data}
                        syncId="anyId"
                        margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="colorHash" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={chartColors.fill} stopOpacity={0.6} />
                                <stop offset="40%" stopColor={chartColors.fill} stopOpacity={0.2} />
                                <stop offset="95%" stopColor={chartColors.fill} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={chartColors.grid}
                            strokeDasharray="10 10"
                            strokeOpacity={0.5}
                        />
                        <XAxis
                            dataKey="time"
                            tick={{ fontSize: 10, fill: chartColors.text, fontWeight: 700, opacity: 0.6 }}
                            axisLine={false}
                            tickLine={false}
                            dy={15}
                        />
                        <YAxis
                            yAxisId="left"
                            domain={[0, maxValue + interval]}
                            ticks={ticks}
                            tickFormatter={(value) => `${value}`}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: chartColors.text, fontWeight: 700, opacity: 0.6 }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '1.5rem',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                color: darkMode ? '#f1f5f9' : '#03436f',
                                padding: '16px',
                                fontWeight: '900'
                            }}
                            itemStyle={{ color: chartColors.stroke }}
                            cursor={{ stroke: chartColors.stroke, strokeWidth: 2, strokeDasharray: '4 4' }}
                        />
                        <Area
                            type="monotone"
                            dataKey="hashrate"
                            stroke={chartColors.stroke}
                            strokeWidth={4}
                            fillOpacity={1}
                            fill="url(#colorHash)"
                            yAxisId="left"
                            animationDuration={2000}
                            dot={{ r: 0, strokeWidth: 2, fill: chartColors.stroke }}
                            activeDot={{ r: 6, strokeWidth: 0, fill: chartColors.stroke }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default SynchronizedAreaChart2;
